import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  AcademicAppointment,
  AcademicCalendarFilters,
  AcademicSchedulingOptions,
  CreateAcademicAppointmentDto,
  UpdateAcademicAppointmentDto,
} from '../../interfaces/academic-appointment';
import { SessionAttendance } from './session-join.service';
import {
  ApiCollectionResponse,
  ApiSingleResponse,
} from '../../interfaces/responses/api-response';

/**
 * Séances académiques côté administration — vue transverse à tous les professeurs.
 *
 * Endpoints:
 *   GET    /api/admin/academic-appointments/calendar?from&to&professorId&subjectId&groupId&studentId
 *   GET    /api/admin/academic-appointments/{id}
 *   POST   /api/admin/academic-appointments
 *   PUT    /api/admin/academic-appointments/{id}
 *   DELETE /api/admin/academic-appointments/{id}
 */
@Injectable({ providedIn: 'root' })
export class AdminAcademicAppointmentService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.BASE_URL}/admin/academic-appointments`;

  calendar(
    filters: AcademicCalendarFilters & { professorId?: number | null } = {},
  ): Observable<ApiCollectionResponse<AcademicAppointment>> {
    let params = new HttpParams();
    if (filters.from) params = params.set('from', filters.from);
    if (filters.to) params = params.set('to', filters.to);
    if (filters.professorId) params = params.set('professorId', filters.professorId);
    if (filters.subjectId) params = params.set('subjectId', filters.subjectId);
    if (filters.groupId) params = params.set('groupId', filters.groupId);
    if (filters.studentId) params = params.set('studentId', filters.studentId);
    if (filters.includeCancelled) params = params.set('includeCancelled', true);

    return this.http.get<ApiCollectionResponse<AcademicAppointment>>(
      `${this.base}/calendar`,
      { params },
    );
  }

  /** Professeurs, matières et groupes — alimente les filtres du calendrier global. */
  options(): Observable<ApiSingleResponse<AcademicSchedulingOptions>> {
    return this.http.get<ApiSingleResponse<AcademicSchedulingOptions>>(
      `${this.base}/options`,
    );
  }

  getById(id: number): Observable<ApiSingleResponse<AcademicAppointment>> {
    return this.http.get<ApiSingleResponse<AcademicAppointment>>(`${this.base}/${id}`);
  }

  /** Bilan de présence d'une séance, reconstruit depuis le journal d'accès. */
  attendance(id: number): Observable<ApiSingleResponse<SessionAttendance>> {
    return this.http.get<ApiSingleResponse<SessionAttendance>>(
      `${this.base}/${id}/attendance`,
    );
  }

  create(
    dto: CreateAcademicAppointmentDto,
  ): Observable<ApiSingleResponse<AcademicAppointment>> {
    return this.http.post<ApiSingleResponse<AcademicAppointment>>(this.base, dto);
  }

  update(
    id: number,
    dto: UpdateAcademicAppointmentDto,
  ): Observable<ApiSingleResponse<AcademicAppointment>> {
    return this.http.put<ApiSingleResponse<AcademicAppointment>>(
      `${this.base}/${id}`,
      dto,
    );
  }

  cancel(
    id: number,
    reason: string,
  ): Observable<ApiSingleResponse<AcademicAppointment>> {
    return this.http.delete<ApiSingleResponse<AcademicAppointment>>(
      `${this.base}/${id}`,
      { body: { reason } },
    );
  }
}
